import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { isEmpty, isEqual } from "lodash";

import { change } from "store/root/actions";
import {
    getEmbodiedCarbon,
    getStandardWindowCodeEmbodiedCarbonDisplay,
    roundEmbodiedKg,
} from "utils/enclosure/embodiedCarbon";
import { getUdefWindowCodeEmbodiedCarbonDisplay } from "utils/enclosure/embc/udefWindowLayers";
import { getUdefEmbodiedCarbon } from "utils/enclosure/embc/udefStandardLayers";
import { getObjValAtPath } from "utils/fields";
import { getCodeDrawerComponentName, getComponentArea, mapCodeDrawerComponentToMeasurements } from "utils/enclosure/codeDrawerMeasurements";

import styles from "./style.module.scss";

const EmbodiedCarbonCalculations = ({
    componentId,
    code,
    componentType,
    modelDataComponents = {},
    selectedComponents = [],
    isUdefCode = false,
    change,
}) => {
    const fieldAccessor = `newForm.codeDrawer.fields.code_embC`;

    const applyAssemblyCalc = useMemo(() => {
        return !isEmpty(selectedComponents) && !isEqual(selectedComponents, [""]);
    }, [selectedComponents]);

    // Calculate the sum of the areas of the selected components in m2
    const totalArea = useMemo(() => {
        if (!applyAssemblyCalc) return null;

        return selectedComponents.reduce((total, componentPath) => {
            const componentData = getObjValAtPath(modelDataComponents, componentPath);
            const measurements = mapCodeDrawerComponentToMeasurements(componentType, componentData);

            if (isEmpty(measurements)) return total;
            
            return total + getComponentArea(componentType, measurements);
        }, 0).toFixed(2);
    }, [selectedComponents]);

    // Calculate total emissions and storage per m2
    const { totalEmissions = 0, totalStorage = 0 } = useMemo(() => {
        if (componentType === "window") {
            const { emissions = null, storage = null } = isUdefCode
                ? getUdefWindowCodeEmbodiedCarbonDisplay({ layers: code[0] })
                : getStandardWindowCodeEmbodiedCarbonDisplay({ layers: code.layers });

            return { totalEmissions: emissions, totalStorage: storage };
        }
        return isUdefCode ? getUdefEmbodiedCarbon({ layers: code }) : getEmbodiedCarbon(code, componentType);
    }, [code, componentType]);

    // Calculate total emissions and storage after assembling the component
    const { assemblyEmissions, assemblyStorage } = useMemo(() => {
        return {
            assemblyEmissions: applyAssemblyCalc ? roundEmbodiedKg(totalEmissions * totalArea) : null,
            assemblyStorage: applyAssemblyCalc ? roundEmbodiedKg(totalStorage * totalArea) : null,
        };
    }, [totalEmissions, totalStorage, totalArea, applyAssemblyCalc]);

    // Get the component name for the display
    const componentName = useMemo(() => getCodeDrawerComponentName(componentType), [componentType]);

    // Save total emissions and storage to code drawer for calculating percentage
    useEffect(() => {
        change(`${fieldAccessor}`, {
            totalEmissions: totalEmissions || 0,
            totalStorage: totalStorage || 0,
        });
    }, [totalEmissions, totalStorage, change]);

    return (
        <div className={styles.outerContainer}>
            <div className={styles.calculationsHeader}>
                <div className={styles.firstColumn}>Calculations</div>
                <div>Emitted:</div>
                <div>Stored:</div>
            </div>
            <div className={styles.calculationsRow}>
                <div className={styles.firstColumn}>Total embodied carbon for assembly:</div>
                <div>
                    <span className={styles.value}>{totalEmissions?.toFixed(2) || "-"}</span>
                    <span className={styles.unit}>kgCO₂/m²</span>
                </div>
                <div>
                    <span className={styles.value}>{totalStorage?.toFixed(2) || "-"}</span>
                    <span className={styles.unit}>kgCO₂/m²</span>
                </div>
            </div>

            <div className={styles.calculationsSeparator} />

            <div className={styles.calculationsRow}>
                <div className={styles.firstColumn}>
                    Total embodied carbon for {componentName}
                    {applyAssemblyCalc ? ` (${totalArea} m²)` : ""}:
                </div>
                <div>
                    <span className={styles.value}>{assemblyEmissions?.toFixed(2) || "-"}</span>
                    <span className={styles.unit}>kgCO₂</span>
                </div>
                <div>
                    <span className={styles.value}>{assemblyStorage?.toFixed(2) || "-"}</span>
                    <span className={styles.unit}>kgCO₂</span>
                </div>
            </div>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    change: (path, value) => dispatch(change(path, value)),
});

export default connect(null, mapDispatchToProps)(EmbodiedCarbonCalculations);
